import { CALL_SERVICE_ENDPOINTS, type VideoCallEndReason } from "@/constants/video-call";
import { CallConfig } from "@/types/video-call";
import type { InterviewFeedbackInput } from "@/types/video-call/feedback";
import { supabase } from "@/lib/supabase";
import * as Sentry from "@sentry/react";
import { NetworkErrorLogger } from "@/utils/networkErrorLogger";
import { logCategorizedError } from "@/utils/errorCategorization";

export interface CallInitResponse {
  livekit_url: string;
  livekit_token: string;
  room_name: string;
  call_id: string;
  audio_config?: {
    echoCancellation: boolean;
    noiseSuppression: boolean;
    autoGainControl: boolean;
    sampleRate: number;
  };
}

export class CallService {
  private static getCallServiceUrl(): string {
    return import.meta.env.VITE_CALL_SERVICE_URL || "http://localhost:8000";
  }

  private static async getApiKey(organizationId: string): Promise<string> {
    const STAGING_KEY = "pk_JJdu2BZEtCOvN6vy969xAQzrT15s6p/j";
    const PROD_KEY = "pk_5krsGX2f";

    const hasWindow = typeof window !== "undefined";
    const hostname = hasWindow ? window.location.hostname.toLowerCase() : null;

    if (hostname) {
      const isStagingLike =
        hostname.includes("localhost") ||
        hostname.includes("127.0.0.1") ||
        hostname.includes("ai22labs") ||
        hostname.includes("staging") ||
        hostname.includes("dev") ||
        hostname.includes("test");
      const isProdLike = hostname.includes("voxi.us");

      if (isStagingLike) return STAGING_KEY;
      if (isProdLike) return PROD_KEY;
      return PROD_KEY;
    }

    // Fallback (no window): fetch the API key from database
    try {
      const { data, error } = await supabase
        .from("api_keys")
        .select("key")
        .eq("organization_id", organizationId)
        .eq("revoked", false)
        .order("created_at", { ascending: false })
        .limit(1);

      if (error) {
        logCategorizedError(error, {
          component: 'CallService',
          operation: 'fetchApiKey',
          organizationId,
          metadata: {
            supabaseQuery: 'organization_api_keys',
            queryFilters: { organization_id: organizationId, is_active: true }
          }
        });
        throw new Error(`Failed to query API keys: ${error.message}`);
      }

      if (!data || data.length === 0) {
        const noKeyError = new Error(`No active API key found for organization ${organizationId}`);
        logCategorizedError(noKeyError, {
          component: 'CallService',
          operation: 'fetchApiKey',
          organizationId,
          metadata: {
            reason: 'no_active_api_key',
            dataLength: data?.length || 0
          }
        });
        throw noKeyError;
      }

      const apiKey = data[0].key as string | null;
      if (!apiKey) {
        const invalidKeyError = new Error(`Invalid API key found for organization ${organizationId}`);
        logCategorizedError(invalidKeyError, {
          component: 'CallService',
          operation: 'fetchApiKey',
          organizationId,
          metadata: {
            reason: 'null_or_empty_api_key',
            hasRecord: true,
            apiKeyType: typeof apiKey
          }
        });
        throw invalidKeyError;
      }

      return apiKey;
    } catch (error) {
      // Only log if it's not one of our expected errors (which are already logged above)
      if (!(error instanceof Error && error.message.includes('organization'))) {
        logCategorizedError(error, {
          component: 'CallService',
          operation: 'fetchApiKey',
          organizationId,
          metadata: {
            errorType: 'unexpected_error'
          }
        });
      }
      throw error;
    }
  }

  /**
   * Initialize a new video call
   */
  static async initializeCall(config: CallConfig): Promise<CallInitResponse> {
    const callServiceUrl = this.getCallServiceUrl();
    const apiKey = await this.getApiKey(config.organizationId);
    
    const startTime = Date.now();
    
    try {
      const response = await fetch(`${callServiceUrl}${CALL_SERVICE_ENDPOINTS.INITIATE}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": apiKey,
        },
        body: JSON.stringify({
          is_web_session: true,
          is_video: true,
          assistant_id: config.assistantId,
          organization_id: config.organizationId,
          metadata: {
            tab_change_detection: config.monitoring.tabChangeDetection,
            external_monitor_detection: config.monitoring.externalMonitorDetection,
            blur_detection: config.monitoring.blurDetection,
            viewport_detection: config.monitoring.viewportDetection,
            echo_cancellation: config.monitoring.echoCancellation,
          },
          variables: config.variables,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        const error = new Error(`API call failed: ${response.status} - ${errorText}`);
        
        NetworkErrorLogger.logNetworkError(error, {
          url: `${callServiceUrl}${CALL_SERVICE_ENDPOINTS.INITIATE}`,
          method: 'POST',
          status: response.status,
          statusText: response.statusText,
          errorMessage: errorText,
          component: 'CallService',
          operation: 'initializeCall',
          metadata: {
            organizationId: config.organizationId,
            assistantId: config.assistantId
          }
        });
        
        throw error;
      }
      
      // Log successful API performance
      NetworkErrorLogger.logApiPerformance('initializeCall', startTime, {
        url: `${callServiceUrl}${CALL_SERVICE_ENDPOINTS.INITIATE}`,
        status: response.status,
        component: 'CallService'
      });

      const tokenData = await response.json();
      console.log("🔊 [CallService] Token data received:", response);
      console.log("🔊 [CallService] Token data received:", tokenData);
      console.log("🔊 [CallService] audio_config in response:", tokenData.audio_config);

      const {
      livekit_url: serverUrl,
      livekit_token: token,
      room_name: roomName,
      call_id: responseCallId,
      audio_config: audioConfig,
      } = tokenData;

      // Validate required fields
      if (!serverUrl || !token || !roomName) {
        const error = new Error(
          `Missing required data - serverUrl: ${!!serverUrl}, token: ${!!token}, roomName: ${!!roomName}`
        );

        console.error('[CallService] Missing required fields in response:', {
          hasServerUrl: !!serverUrl,
          hasToken: !!token,
          hasRoomName: !!roomName,
          tokenData
        });

        // Log missing fields error with categorized logging
        logCategorizedError(error, {
          component: 'CallService',
          operation: 'validateCallInitResponse',
          organizationId: config.organizationId,
          metadata: {
            hasServerUrl: !!serverUrl,
            hasToken: !!token,
            hasRoomName: !!roomName,
            tokenDataKeys: Object.keys(tokenData || {}),
            assistantId: config.assistantId
          }
        });

        throw error;
      }

      const result = {
        livekit_url: serverUrl,
        livekit_token: token,
        room_name: roomName,
        call_id: responseCallId,
        audio_config: audioConfig,
      };
      
      console.log("🔊 [CallService] Returning result:", result);
      console.log("🔊 [CallService] audio_config being returned:", result.audio_config);
      
      return result;
    } catch (error) {
      // Handle network errors
      if (NetworkErrorLogger.isNetworkError(error)) {
        NetworkErrorLogger.logNetworkError(error, {
          url: `${callServiceUrl}${CALL_SERVICE_ENDPOINTS.INITIATE}`,
          method: 'POST',
          component: 'CallService',
          operation: 'initializeCall',
          metadata: {
            organizationId: config.organizationId,
            errorType: 'network_failure'
          }
        });
        
        throw new Error('Network error: Unable to connect to call service. Please check your internet connection.');
      }
      
      // Re-throw other errors
      throw error;
    }
  }

  /**
   * End a video call
   */
  static async endCall(callId: string, organizationId: string, endedReason?: VideoCallEndReason): Promise<void> {
    const callServiceUrl = this.getCallServiceUrl();
    const apiKey = await this.getApiKey(organizationId);
    
    try {
      // First, update the ended_reason if provided
      if (endedReason) {
        await this.updateEndedReason(callId, organizationId, endedReason);
      }
      
      // Then call the GET endpoint to get call details (which may trigger other ending logic)
      const response = await fetch(`${callServiceUrl}${CALL_SERVICE_ENDPOINTS.END(callId)}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": apiKey,
        },
      });

      if (!response.ok) {
        const error = new Error(`Failed to end call: ${response.status}`);
        
        console.error('[CallService] Failed to end call:', {
          status: response.status,
          callId,
          organizationId,
          endedReason
        });
        
        Sentry.logger.error('Failed to end call', error, {
          component: 'CallService',
          httpStatus: response.status,
          callId,
          organizationId,
          endedReason
        });
        
        throw error;
      }
    } catch (error) {
      // Handle network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.error('[CallService] Network error during call end:', {
          message: error.message,
          callId,
          organizationId
        });
        
        Sentry.logger.error('Network error during call end', error, {
          component: 'CallService',
          errorType: 'network',
          callId,
          organizationId
        });
        
        throw new Error('Network error: Unable to end call properly. Please check your connection.');
      }
      
      throw error;
    }
  }

  /**
   * Update the ended_reason for a call
   */
  static async updateEndedReason(callId: string, organizationId: string, endedReason: VideoCallEndReason): Promise<void> {
    const callServiceUrl = this.getCallServiceUrl();
    const apiKey = await this.getApiKey(organizationId);
    
    try {
      const response = await fetch(`${callServiceUrl}/api/call/${callId}/ended-reason`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": apiKey,
        },
        body: JSON.stringify({
          ended_reason: endedReason,
        }),
      });

      if (!response.ok) {
        const error = new Error(`Failed to update ended reason: ${response.status}`);
        
        console.error('[CallService] Failed to update ended reason:', {
          status: response.status,
          callId,
          endedReason
        });
        
        Sentry.logger.error('Failed to update ended reason', error, {
          component: 'CallService',
          httpStatus: response.status,
          callId,
          endedReason
        });
        
        throw error;
      }
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.error('[CallService] Network error updating ended reason:', {
          message: error.message,
          callId,
          endedReason
        });
        
        Sentry.logger.error('Network error updating ended reason', error, {
          component: 'CallService',
          errorType: 'network',
          callId,
          endedReason
        });
      }
      
      throw error;
    }
  }

  /**
   * Send cleanup signal for non-user-initiated disconnections
   */
  static async sendCleanupSignal(callId: string): Promise<void> {
    try {
      const callServiceUrl = this.getCallServiceUrl();
      const response = await fetch(`${callServiceUrl}${CALL_SERVICE_ENDPOINTS.END(callId)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      
      if (!response.ok) {
        console.error('[CallService] Cleanup signal failed:', {
          status: response.status,
          callId
        });
        
        Sentry.logger.warn('Cleanup signal failed', {
          component: 'CallService',
          httpStatus: response.status,
          callId
        });
      }
    } catch (error) {
      console.error('[CallService] Error in cleanup signal:', {
        error,
        callId
      });
      
      Sentry.logger.error('Error in cleanup signal', error, {
        component: 'CallService',
        callId,
        errorType: error instanceof TypeError ? 'network' : 'unknown'
      });
      
      throw error;
    }
  }

  /**
   * Immediately terminate call due to violation and stop agent
   */
  static async terminateForViolation(
    callId: string,
    organizationId: string,
    violationType: string, 
    reason: string,
    additionalData?: any
  ): Promise<void> {
    const callServiceUrl = this.getCallServiceUrl();
    const apiKey = await this.getApiKey(organizationId);
    
    try {
      const response = await fetch(`${callServiceUrl}/api/call/${callId}/terminate-violation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": apiKey,
        },
        body: JSON.stringify({
          violation_type: violationType,
          reason: reason,
          timestamp: new Date().toISOString(),
          ...additionalData
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Failed to terminate call: ${response.status} - ${errorText}`);

        const error = new Error(`Failed to terminate call for violation: ${response.status}`);
        logCategorizedError(error, {
          component: 'CallService',
          operation: 'terminateForViolation',
          callId,
          organizationId,
          metadata: {
            httpStatus: response.status,
            errorText,
            violationType,
            reason,
            terminateUrl: `${callServiceUrl}/api/call/${callId}/terminate-violation`
          }
        });

        throw error;
      }

      await response.json();
    } catch (error) {
      console.error(`Error terminating call for violation:`, error);

      // Only log if it's not already logged (avoid double logging)
      if (!(error instanceof Error && error.message.includes('Failed to terminate call for violation'))) {
        logCategorizedError(error, {
          component: 'CallService',
          operation: 'terminateForViolation',
          callId,
          organizationId,
          metadata: {
            violationType,
            reason,
            errorType: 'unexpected_error'
          }
        });
      }

      throw error;
    }
  }

  /**
   * Submit interview feedback for a call log
   */
  static async submitInterviewFeedback(
    callId: string,
    organizationId: string,
    feedbackInput: InterviewFeedbackInput
  ): Promise<void> {
    const callServiceUrl = this.getCallServiceUrl();
    const apiKey = await this.getApiKey(organizationId);

    try {
      const response = await fetch(`${callServiceUrl}/api/call/${callId}/interview-feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": apiKey,
        },
        body: JSON.stringify({
          rating: feedbackInput.rating,
          feedback_text: feedbackInput.feedback_text?.trim() || undefined,
        }),
      });

      if (!response.ok) {
        let errorMessage = `Failed to submit interview feedback: ${response.status}`;
        try {
          const responseText = await response.text();
          if (responseText) {
            try {
              const parsed = JSON.parse(responseText);
              errorMessage = parsed?.message || parsed?.error || errorMessage;
            } catch {
              errorMessage = responseText;
            }
          }
        } catch (parseError) {
          console.warn("[CallService] Unable to parse feedback submission error response:", parseError);
        }

        const error = new Error(errorMessage);

        console.error("[CallService] Failed to submit interview feedback:", {
          status: response.status,
          callId,
          errorMessage,
        });

        Sentry.logger.error("Failed to submit interview feedback", error, {
          component: "CallService",
          httpStatus: response.status,
          callId,
        });

        throw error;
      }
    } catch (error) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        console.error("[CallService] Network error submitting interview feedback:", {
          message: error.message,
          callId,
          organizationId,
        });

        Sentry.logger.error("Network error submitting interview feedback", error, {
          component: "CallService",
          errorType: "network",
          callId,
          organizationId,
        });

        throw new Error("Network error: Unable to submit feedback. Please check your connection.");
      }

      throw error;
    }
  }
}
